import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from 'react-helmet-async';

const LuxuryTravelAdvisorsPage = () => {
  const { pathname } = useLocation();
  const studioPublicUrl =
    import.meta.env.VITE_STUDIO_PUBLIC_URL ||
    import.meta.env.VITE_APP_URL ||
    "https://studio.axonall.com";

  const heroBackgroundImage =
    "https://s3.eu-north-1.amazonaws.com/spaceaxonall.com/origin+mvp+assets/landing_page/axonall-coromandel.jpg";

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [pathname]);

  const carouselItems = [
    {
      title: "Axonall Studio",
      description: "An intelligent private environment for high-value travel. Step into Axonall Studio, the private operating environment designed for Reserve advisors. Discover curated product, move with greater precision, and work within a more elevated ecosystem built for the future of high-value travel.",
      image: "https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/landing_page/refine/loacations/london.jpeg",
      alt: "London reserve experience",
    },
    {
      title: "Curated Access",
      description: "Distinctive product, beyond the ordinary. Unlock access to a more selective world of stays, experiences, and differentiated opportunities shaped around Axonall's standards of quality, design, and emotional resonance, for clients who expect more than the usual luxury circuit.",
      image: "https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/landing_page/refine/New-Hotels-Mallorca-Square-2.jpg",
      alt: "Mallorca reserve access",
    },
    {
      title: "Journey Artisan Support",
      description: "Human expertise behind every booking. Every booking is backed by Axonall's Journey Artisans, adding a high-touch layer of support, care, and judgement for more nuanced requests, complex itineraries, and exceptional client delivery.",
      image: "https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/landing_page/refine/Cap-Rocat-Stage-M.jpg",
      alt: "Cap Rocat Journey Artisan support",
    },
    {
      title: "Intelligence",
      description: "Stay ahead of where luxury travel is going. Reserve gives advisors access to insight, learning, and future-facing developments across client expectations, service, technology, and the evolving world of intelligent luxury travel.",
      image: "https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/landing_page/refine/Screenshot_2025-06-05_at_2.02.32%C3%A2__PM.webp",
      alt: "Reserve intelligence and future-facing insights",
    },
  ];

  const [[currentIndex, direction], setCurrentIndex] = useState([0, 0]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(([prevIndex, prevDirection]) => [
        prevIndex === carouselItems.length - 1 ? 0 : prevIndex + 1,
        1,
      ]);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handlePrev = () => {
    setCurrentIndex(([prevIndex, prevDirection]) => [
      prevIndex === 0 ? carouselItems.length - 1 : prevIndex - 1,
      -1,
    ]);
  };

  const handleNext = () => {
    setCurrentIndex(([prevIndex, prevDirection]) => [
      prevIndex === carouselItems.length - 1 ? 0 : prevIndex + 1,
      1,
    ]);
  };

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 200 : -200,
      opacity: 0,
      scale: 0.9,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
        duration: 0.8,
        ease: "easeOut",
      },
    },
    exit: (direction) => ({
      x: direction < 0 ? 200 : -200,
      opacity: 0,
      scale: 0.9,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
        duration: 0.6,
        ease: "easeIn",
      },
    }),
  };

  const textVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      boxShadow: "0px 8px 25px rgba(0, 0, 0, 0.3)",
      transition: { duration: 0.2, ease: "easeInOut" },
    },
  };

  const featureCards = [
    {
      title: "A Selected Circle",
      description: "Membership is reserved for advisors who align with Axonall's standards, taste, and long-term vision for high-value travel."
    },
    {
      title: "More Distinctive Access",
      description: "Offer clients a more selective world of stays, experiences, and differentiated opportunities beyond the ordinary."
    },
    {
      title: "A Voice at the Table",
      description: "Reserve members are part of a selected advisor group with meaningful proximity to what comes next - contributing perspective on how intelligent systems, frontier experiences, and the future service standards of high-value travel."
    },
    {
      title: "Meaningful Professional Exchange",
      description: "Connect within a more trusted advisor ecosystem built around quality, relevance, and stronger business dialogue."
    },
    {
      title: "Future-Facing Intelligence",
      description: "Gain access to learning, insight, and evolving perspectives across luxury travel, service, technology, and changing client expectations."
    },
    {
      title: "Curated Member Opportunities",
      description: "From private sessions to selected visibility and roundtables, Reserve creates opportunities for deeper participation across the Axonall ecosystem."
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <HelmetProvider>
        <Helmet>
          <title>Axonall Reserve | Invite-Only Private Advisor Circle</title>
          <meta name="description" 
            content="Axonall Reserve brings together selected advisors who shape where high-value travel is going. An invite-only private advisor circle for luxury travel professionals." />
          <meta name="keywords" 
            content="Axonall Reserve, Private Advisor Circle, Luxury Travel Advisors, High-Value Travel, Axonall Studio, Journey Artisans, Curated Access, Black Key Access" />
          <meta property="og:title" content="Axonall Reserve - Invite-Only Private Advisor Circle" />
          <meta property="og:description" 
            content="Reserve brings together selected advisors who shape where high-value travel is going. Curated product, meaningful exchange, and future-facing technology." />
          <meta property="og:image" content="https://images.pexels.com/photos/261102/pexels-photo-261102.jpeg" />
          <meta property="og:url" content={`${studioPublicUrl}/luxurytravel-advisors`} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Axonall Reserve - Invite-Only Private Advisor Circle" />
          <meta name="twitter:description" 
            content="Reserve brings together selected advisors who shape where high-value travel is going. Curated product, meaningful exchange, and future-facing technology." />
          <meta name="twitter:image" content="https://images.pexels.com/photos/261102/pexels-photo-261102.jpeg" />
          <link rel="canonical" href={`${studioPublicUrl}/luxurytravel-advisors`} />
        </Helmet>
      </HelmetProvider>

      {/* Hero Section with Image Background */}
      <div className="flex w-full items-center justify-center min-h-screen bg-black flex-col px-4 sm:px-6 relative overflow-hidden">
        {/* Image Background */}
        <div
          aria-hidden="true"
          role="presentation"
          className="absolute inset-0 w-full h-full bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${heroBackgroundImage})` }}
        />
        
        {/* Black Overlay */}
        <div className="absolute inset-0 bg-black-5"></div>
        
        {/* Centered Content */}
        <div className="relative h-full flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-center text-white"
          >
            <div className="mb-8 mt-20">
              <span className="inline-block bg-white/20 px-8 py-3 rounded-none text-sm font-poppinsMedium text-white border border-white/30 tracking-wider">
                AN INVITE-ONLY PRIVATE ADVISOR CIRCLE
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-AXONALLPrimeRegular leading-tight mb-8 text-white">
              AXONALL RESERVE
            </h1>
            <p className="sm:text-lg md:text-xl lg:text-2xl max-w-3xl mx-auto mt-8 font-poppinsLight text-white/90 leading-relaxed">
              Reserve brings together selected advisors who do more than sell exceptional travel. Together, we shape where high-value travel is going, from intelligent systems and frontier experiences to the expectations and standards that define modern luxury.
            </p>
            <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center w-full sm:w-auto px-2 sm:px-0">
              <Link to="/luxurytravel-advisors-register" className="w-full sm:w-auto">
                <motion.button
                  type="button"
                  className="bg-white text-black px-6 sm:px-8 md:px-10 py-3 sm:py-4 text-base sm:text-lg font-poppinsMedium hover:bg-gray-100 transform transition-all duration-300 ease-in-out border border-white w-full sm:w-auto"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Apply for Reserve
                </motion.button>
              </Link>
              <Link
                to="/login"
                className="bg-transparent text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 text-base sm:text-lg font-poppinsMedium border border-white hover:bg-white hover:text-black transform transition-all duration-300 ease-in-out w-full sm:w-auto text-center"
              >
                Enter Studio
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Why Axonall Reserve — dark band + stats */}
      <section
        id="intro-section"
        className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-12 lg:px-32 bg-zinc-900"
      >
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-ptSerifBold text-white tracking-[0.12em] mb-8 sm:mb-10">
              WHY AXONALL RESERVE
            </h2>
            <p className="text-base sm:text-lg md:text-xl font-ptSerifRegular text-white leading-relaxed mb-6 sm:mb-8 break-words">
              Reserve is Axonall&apos;s private advisor circle who want more than access alone. It brings together an influential advisor group serving high-value clients.
            </p>
            <p className="text-base sm:text-lg md:text-xl font-ptSerifRegular text-white leading-relaxed break-words mb-10 sm:mb-12">
              Reserve offers a more elevated way of working built around curated product, meaningful exchange, future-facing technology, and a voice in where high-value travel is heading next.
            </p>
            <div
              className="mx-auto max-w-md rounded-sm border border-white/20 bg-white/5 px-6 sm:px-10 py-8 sm:py-10"
              role="region"
              aria-label="Axonall Reserve at a glance"
            >
              <ul className="space-y-4 sm:space-y-5 text-center font-ptSerifRegular text-base sm:text-lg">
                <li>
                  <span className="font-ptSerifBold text-white">150+</span>{" "}
                  <span className="text-gray-400">Countries</span>
                </li>
                <li>
                  <span className="font-ptSerifBold text-white">10K+</span>{" "}
                  <span className="text-gray-400">Properties</span>
                </li>
                <li>
                  <span className="font-ptSerifBold text-white">Carbon-Aware</span>{" "}
                  <span className="text-gray-400">Travel Tools</span>
                </li>
                <li>
                  <span className="font-ptSerifBold text-white">Journey Artisan</span>{" "}
                  <span className="text-gray-400">Support</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Shaping the Future Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-12 lg:px-32 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-AXONALLPrimeRegular text-black mb-8">
              SHAPING THE FUTURE OF LUXURY TRAVEL
            </h2>
            <div className="w-32 h-px bg-black mx-auto mb-8"></div>
            <p className="text-base sm:text-lg md:text-xl font-poppinsLight text-black max-w-3xl mx-auto leading-relaxed break-words">
              Axonall Reserve brings together selected advisors who share Axonall's vision for a more intelligent, trusted, and distinctive future in high-value travel.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Modern Carousel Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-12 lg:px-32 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-AXONALLPrimeRegular text-black mb-4 sm:mb-6 px-2 sm:px-0">
              AXONALL RESERVE
            </h2>
            <p className="text-base sm:text-lg font-poppinsLight text-black max-w-2xl mx-auto px-2 sm:px-0">
              Discover curated product, Journey Artisan support, and future-facing intelligence
            </p>
          </motion.div>

          <div className="relative">
            {/* Main Carousel Container */}
            <div className="relative h-[500px] sm:h-[550px] md:h-[600px] overflow-hidden bg-white border border-gray-200">
              <AnimatePresence initial={false} custom={direction} mode="wait">
                {carouselItems.map(
                  (item, index) =>
                    index === currentIndex && (
                      <motion.div
                        key={index}
                        className="absolute inset-0"
                        custom={direction}
                        variants={variants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 h-full">
                          {/* Image Section */}
                          <div className="relative overflow-hidden h-[250px] sm:h-[300px] md:h-full">
                            <motion.img
                              src={item.image}
                              alt={item.alt || item.title}
                              className="w-full h-full object-cover"
                              initial={{ scale: 1.1 }}
                              animate={{ scale: 1 }}
                              transition={{ duration: 1.2, ease: "easeOut" }}
                            />
                            <div className="absolute inset-0 bg-black/20"></div>
                          </div>
                          
                          {/* Content Section */}
                          <div className="p-4 sm:p-6 md:p-8 lg:p-16 flex flex-col justify-center overflow-y-auto">
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.3, duration: 0.6 }}
                            >
                              <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-AXONALLPrimeRegular text-black mb-4 sm:mb-6 md:mb-8 leading-tight break-words">
                                {item.title}
                              </h3>
                              <p className="text-base sm:text-lg font-poppinsLight text-black leading-relaxed break-words">
                                {item.description}
                              </p>
                            </motion.div>
                          </div>
                        </div>
                      </motion.div>
                    )
                )}
              </AnimatePresence>
            </div>

            {/* Modern Navigation */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0 mt-6 sm:mt-8">
              {/* Left Navigation */}
              <button
                onClick={handlePrev}
                className="group flex items-center space-x-2 sm:space-x-3 bg-white hover:bg-black text-black hover:text-white px-4 sm:px-6 py-2 sm:py-3 border border-black transition-all duration-300 ease-in-out w-full sm:w-auto justify-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:-translate-x-1"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                <span className="font-poppinsMedium text-xs sm:text-sm">Previous</span>
              </button>

              {/* Progress Indicator */}
              <div className="flex space-x-2 order-first sm:order-none">
                {carouselItems.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex([index, index > currentIndex ? 1 : -1])}
                    className={`transition-all duration-300 ${
                      index === currentIndex 
                        ? 'w-8 sm:w-12 h-1 bg-black' 
                        : 'w-6 sm:w-8 h-1 bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>

              {/* Right Navigation */}
              <button
                onClick={handleNext}
                className="group flex items-center space-x-2 sm:space-x-3 bg-white hover:bg-black text-black hover:text-white px-4 sm:px-6 py-2 sm:py-3 border border-black transition-all duration-300 ease-in-out w-full sm:w-auto justify-center"
              >
                <span className="font-poppinsMedium text-xs sm:text-sm">Next</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Slide Counter */}
            <div className="text-center mt-6">
              <span className="font-poppinsMedium text-sm text-black">
                {currentIndex + 1} / {carouselItems.length}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Inside Axonall Reserve */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-12 lg:px-32 bg-gray-50 pb-24 sm:pb-28 md:pb-20">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-AXONALLPrimeRegular text-black mb-4 sm:mb-6 px-2 sm:px-0">
              INSIDE AXONALL RESERVE
            </h2>
            <p className="text-base sm:text-lg font-poppinsLight text-black max-w-2xl mx-auto px-2 sm:px-0">
              What membership offers selected advisors
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {featureCards.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-4 sm:p-6 md:p-8 border border-gray-200 hover:border-black transition-all duration-300 ease-in-out hover:-translate-y-2"
              >
                <h3 className="text-lg sm:text-xl font-AXONALLPrimeRegular text-black mb-3 sm:mb-4 break-words">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-black font-poppinsLight leading-relaxed break-words">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Black Key Access Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-12 lg:px-32 bg-black text-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-AXONALLPrimeRegular mb-6 sm:mb-8 px-2 sm:px-0">
              BLACK KEY ACCESS
            </h2>
            <p className="text-base sm:text-lg md:text-xl font-poppinsLight text-white/90 max-w-2xl mx-auto px-2 sm:px-0 leading-relaxed">
              Black Key Access is granted selectively to a limited number of Reserve members. It reflects the highest level of access and recognition within the Reserve circle.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-12 lg:px-32 bg-white pb-24 sm:pb-28 md:pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-AXONALLPrimeRegular text-black mb-6 sm:mb-8 px-2 sm:px-0">
              WANT TO LEARN MORE ABOUT AXONALL RESERVE?
            </h2>
            <p className="text-base sm:text-lg md:text-xl font-poppinsLight text-black mb-8 sm:mb-10 md:mb-12 max-w-2xl mx-auto px-2 sm:px-0">
              Axonall Reserve is a selective circle for luxury travel advisors aligned with Axonall's standards and future direction. For assistance, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center px-2 sm:px-0">
              <Link to="/luxurytravel-advisors-register" className="w-full sm:w-auto">
                <motion.button
                  type="button"
                  className="bg-black text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 text-base sm:text-lg font-poppinsMedium hover:bg-gray-800 transform transition-all duration-300 ease-in-out border border-black w-full sm:w-auto"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Apply for Reserve
                </motion.button>
              </Link>
              <Link
                to="/login"
                className="bg-transparent text-black px-6 sm:px-8 md:px-10 py-3 sm:py-4 text-base sm:text-lg font-poppinsMedium border border-black hover:bg-black hover:text-white transform transition-all duration-300 ease-in-out w-full sm:w-auto text-center"
              >
                Enter Studio
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LuxuryTravelAdvisorsPage;
